import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createAssignment, resetAssignmentState, fetchAssignments } from '../../redux/assignment/assignment';
import Cookies from 'js-cookie';
import UserSelect from '../Global/SelectProfile';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { X, Plus, Trash2, CircleUser, Bold, List, ListOrdered } from 'lucide-react';
import PhotoUploader from '../Global/uploader';

const xorDecrypt = (encrypted, secretKey = '28032002') => {
  try {
    const decoded = atob(encrypted);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ secretKey.charCodeAt(i % secretKey.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

const getAuthToken = () => {
  const encryptedToken = Cookies.get('authToken');
  if (!encryptedToken) {
    return null;
  }

  const token = xorDecrypt(encryptedToken);
  if (!token) {
    console.warn('Failed to decrypt auth token');
    return null;
  }

  return token;
};

const CreateTask = ({ isOpen, onClose, selectedProjectId }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.assignments);
  const [uid] = useState(Cookies.get('userUid') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const taskEditorRef = useRef(null);

  console.log(selectedProjectId);

  const [formData, setFormData] = useState({
    assignedPerson: '',
    task: '',
    startDate: new Date(),
    endDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
    isCompleted: false,
    projectId: selectedProjectId,
    urls: '',
    status: 'in-progress',
    createdBy: uid,
    comment: '',
    commentUserId: uid,
    issue: '',
    issueUserId: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Rich text editor functions
  const handleBold = () => {
    if (taskEditorRef.current) {
      taskEditorRef.current.focus();
      document.execCommand('bold', false, null);
      // Update form data immediately after formatting
      const content = taskEditorRef.current.innerHTML;
      setFormData(prev => ({
        ...prev,
        task: content
      }));
    }
  };

  const handleBulletList = () => {
    if (taskEditorRef.current) {
      taskEditorRef.current.focus();
      document.execCommand('insertUnorderedList', false, null);
      // Force update after list creation
      setTimeout(() => {
        if (taskEditorRef.current) {
          const content = taskEditorRef.current.innerHTML;
          setFormData(prev => ({
            ...prev,
            task: content
          }));
        }
      }, 10);
    }
  };

  const handleNumberedList = () => {
    if (taskEditorRef.current) {
      taskEditorRef.current.focus();
      document.execCommand('insertOrderedList', false, null);
      // Force update after list creation
      setTimeout(() => {
        if (taskEditorRef.current) {
          const content = taskEditorRef.current.innerHTML;
          setFormData(prev => ({
            ...prev,
            task: content
          }));
        }
      }, 10);
    }
  };

  const handleTaskEditorKeyDown = (e) => {
    // Handle Ctrl+B for bold
    if (e.ctrlKey && e.key === 'b') {
      e.preventDefault();
      handleBold();
      return;
    }
    
    // Handle Enter key for better list formatting
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        
        // Check if we're inside a list item
        const listItem = node.nodeType === 3 ? node.parentElement : node;
        if (listItem.tagName === 'LI') {
          // If at the end of a list item, create a new list item
          if (range.endOffset === node.length) {
            e.preventDefault();
            
            // Create a new list item
            const newListItem = document.createElement('li');
            listItem.parentNode.insertBefore(newListItem, listItem.nextSibling);
            
            // Move cursor to the new list item
            const newRange = document.createRange();
            newRange.setStart(newListItem, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            // Update form data
            setTimeout(() => {
              if (taskEditorRef.current) {
                const content = taskEditorRef.current.innerHTML;
                setFormData(prev => ({
                  ...prev,
                  task: content
                }));
              }
            }, 10);
            return;
          }
        }
      }
      
      // Default behavior for non-list items
      setTimeout(() => {
        if (taskEditorRef.current) {
          const content = taskEditorRef.current.innerHTML;
          setFormData(prev => ({
            ...prev,
            task: content
          }));
        }
      }, 10);
    }
  };

  const handleTaskEditorInput = (e) => {
    const content = e.target.innerHTML;
    setFormData(prev => ({
      ...prev,
      task: content
    }));
  };

  // Handle paste events to maintain formatting
  const handleTaskEditorPaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, paste);
    
    setTimeout(() => {
      if (taskEditorRef.current) {
        const content = taskEditorRef.current.innerHTML;
        setFormData(prev => ({
          ...prev,
          task: content
        }));
      }
    }, 10);
  };

  // Fetch project data when selectedProjectId changes
  useEffect(() => {
    const fetchProjectData = async () => {
      // If selectedProjectId is "general" or empty, don't fetch project data
      if (selectedProjectId === "general" || !selectedProjectId) {
        setProjectTeamMembers([]);
        return;
      }
      
      setIsLoadingProject(true);
      try {
        const token = getAuthToken();
        if (!token) {
          return;
        }
        const response = await fetch(`https://internalApi.sequoia-print.com/api/projets/${selectedProjectId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const projectData = await response.json();
        console.log(projectData);
        
        // Extract team members from project data including team lead
        const members = [];
        
        // Add team lead if available
        if (projectData.team_lead) {
          members.push({
            value: projectData.team_lead.uid,
            label: projectData.team_lead.username || 
                   `${projectData.team_lead.firstname || ''} ${projectData.team_lead.lastname || ''}`.trim() || 
                   projectData.team_lead.uid
          });
        }
        
        // Add team members if available
        if (projectData.team_members) {
          projectData.team_members.forEach(member => {
            members.push({
              value: member.uid,
              label: member.username || 
                     `${member.firstname || ''} ${member.lastname || ''}`.trim() || 
                     member.uid
            });
          });
        }
        
        setProjectTeamMembers(members);
      } catch (error) {
        console.error('Error fetching project data:', error);
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchProjectData();
  }, [selectedProjectId]);

  // Update projectId when selectedProjectId changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      projectId: selectedProjectId
    }));
  }, [selectedProjectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleUploadSuccess = (fileUrl) => {
    setUploadedFiles(prev => [...prev, fileUrl]);
  };

  const handleUploadError = (error) => {
    console.error('File upload error:', error);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      assignedPerson: '',
      task: '',
      startDate: new Date(),
      endDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
      isCompleted: false,
      projectId: selectedProjectId,
      urls: '',
      status: 'in-progress',
      createdBy: uid,
      comment: '',
      commentUserId: uid,
      issue: '',
      issueUserId: ''
    });
    setUploadedFiles([]);
    
    // Clear the rich text editor
    if (taskEditorRef.current) {
      taskEditorRef.current.innerHTML = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Get the HTML content from the editor for submission
    const taskContent = taskEditorRef.current ? taskEditorRef.current.innerHTML.trim() : formData.task;
    
    // Validate that task content exists
    if (!taskContent || taskContent === '<br>' || taskContent === '') {
      alert('Please enter a task description');
      setIsSubmitting(false);
      return;
    }

    const submissionData = {
      ...formData,
      task: taskContent,
      urls: uploadedFiles.join(','),
      projectId: selectedProjectId
    };

    console.log('Submitting data with formatted task:', submissionData);
    console.log('Task HTML content:', taskContent);

    // Use optimistic approach - close modal and refresh regardless of response
    try {
      // Fire and forget approach since we know the task is being created
      dispatch(createAssignment(submissionData));

      // Close modal immediately
      resetForm();
      onClose();

      // Refresh data after a short delay to ensure backend processing
      setTimeout(() => {
        dispatch(fetchAssignments());
      }, 1000);

    } catch (error) {
      console.error('Error in handleSubmit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    dispatch(resetAssignmentState());
    setIsSubmitting(false);
    onClose();
  };

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetAssignmentState());
      setIsSubmitting(false);
    };
  }, [dispatch]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      dispatch(resetAssignmentState());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Create New Task</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Task Description with Rich Text Editor */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Description*</label>
              
              {/* Rich Text Toolbar */}
              <div className="flex items-center gap-1 p-2 border border-b-0 rounded-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleBold}
                  className="p-1.5 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                  title="Bold (Ctrl+B)"
                  disabled={isSubmitting}
                >
                  <Bold size={16} />
                </button>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                <button
                  type="button"
                  onClick={handleBulletList}
                  className="p-1.5 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                  title="Bullet List"
                  disabled={isSubmitting}
                >
                  <List size={16} />
                </button>
                
                <button
                  type="button"
                  onClick={handleNumberedList}
                  className="p-1.5 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                  title="Numbered List"
                  disabled={isSubmitting}
                >
                  <ListOrdered size={16} />
                </button>
              </div>

              {/* Rich Text Editor */}
              <div
                ref={taskEditorRef}
                contentEditable={!isSubmitting}
                onInput={handleTaskEditorInput}
                onKeyDown={handleTaskEditorKeyDown}
                onPaste={handleTaskEditorPaste}
                className="w-full p-3 border border-t-0 rounded-b min-h-[100px] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                style={{
                  minHeight: '100px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
                data-placeholder="Enter task description... Use Ctrl+B for bold text"
                suppressContentEditableWarning={true}
              />
              
              <p className="text-xs text-gray-500 mt-1">
                Use <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-xs">Ctrl+B</kbd> for bold, 
                or use the toolbar buttons for formatting. HTML formatting will be preserved.
              </p>

              {/* Add CSS for placeholder and list styling */}
              <style jsx>{`
                div[contenteditable]:empty:before {
                  content: attr(data-placeholder);
                  color: #9CA3AF;
                  pointer-events: none;
                  position: absolute;
                }
                div[contenteditable] ul {
                  list-style-type: disc;
                  padding-left: 20px;
                  margin: 10px 0;
                }
                div[contenteditable] ol {
                  list-style-type: decimal;
                  padding-left: 20px;
                  margin: 10px 0;
                }
                div[contenteditable] li {
                  margin: 5px 0;
                  padding-left: 5px;
                }
                div[contenteditable] strong, div[contenteditable] b {
                  font-weight: bold;
                }
              `}</style>
            </div>

            {/* Assigned Person */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To*</label>
              {selectedProjectId && selectedProjectId !== "general" && projectTeamMembers.length > 0 ? (
                <select
                  value={formData.assignedPerson}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedPerson: e.target.value }))}
                  className="w-full p-2 border rounded"
                  required
                  disabled={isSubmitting || isLoadingProject}
                >
                  <option value="">Select a team member</option>
                  {projectTeamMembers.map(member => (
                    <option key={member.value} value={member.value}>
                      {member.label}
                    </option>
                  ))}
                </select>
              ) : (
                <UserSelect
                  value={formData.assignedPerson}
                  onChange={(value) => setFormData(prev => ({ ...prev, assignedPerson: value }))}
                  disabled={isSubmitting}
                />
              )}
              {isLoadingProject && (
                <p className="text-sm text-gray-500 mt-1">Loading team members...</p>
              )}
              <div 
                className='mt-4 flex flex-row gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors'
                onClick={() => setFormData(prev => ({ ...prev, assignedPerson: uid }))}
              >
                <CircleUser className={formData.assignedPerson === uid ? 'text-orange-500' : 'text-gray-500'} />
                <p className={`${formData.assignedPerson === uid ? 'text-orange-500 font-medium' : 'text-gray-500'}`}>
                  Assign Myself
                </p>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status*</label>
              <select
                name="status"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full p-2 border rounded"
                required
                disabled={isSubmitting}
              >
                <option value="in-progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date*</label>
              <DatePicker
                selected={formData.startDate}
                onChange={(date) => handleDateChange(date, 'startDate')}
                className="w-full p-2 border rounded"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date*</label>
              <DatePicker
                selected={formData.endDate}
                onChange={(date) => handleDateChange(date, 'endDate')}
                className="w-full p-2 border rounded"
                minDate={formData.startDate}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* File Upload */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload Files</label>
              {!isSubmitting && (
                <PhotoUploader
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                >
                  <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 transition-colors cursor-pointer">
                    <Plus className="w-6 h-6 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 2MB</p>
                  </div>
                </PhotoUploader>
              )}

              {uploadedFiles.length > 0 && (
                <div className="mt-2 space-y-2">
                  {uploadedFiles.map((fileUrl, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline text-sm truncate"
                      >
                        {fileUrl.split('/').pop()}
                      </a>
                      {!isSubmitting && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Success message */}
          {isSubmitting && (
            <div className="mb-4 text-green-500 text-sm">
              Creating task... The modal will close automatically.
            </div>
          )}

          {/* Error message - only show if not submitting */}
          {error && !isSubmitting && (
            <div className="mb-4 text-red-500 text-sm">
              {typeof error === 'string' ? error : 'Failed to create task'}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : (
                <>
                  <Plus size={16} />
                  Create Task
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTask;